import React from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import _ from 'lodash';
import {
  format, subDays, addDays,
} from 'date-fns';
import {
  Box, Text, Image, Layer, Button,
} from 'grommet';
import {
  Next, Previous, FormClose,
} from 'grommet-icons';
import { Spinning, AppButton, DatetimeCalendar } from '@Components/Control';
import { CurrentDateContext, useAppTheme } from '@Components/Context';
import { ProductFormSingleTextInput } from '@Components/Partial/Product/NewSearch';
import { paths } from '@Components/configs';
import { localAsset } from '@Helpers/asset';
import { shortenInt, pluralize } from '@Helpers/util';
import {
  StyledText,
  StyledRaisedContainer,
  StyledSimpleDrop,
  StyledSelectMenu,
  StyledPaginationArrow,
  StyledSVG,
  StyledTextInput,
  StyledSearchInputClear,
  StyledRangeSelect,
} from './StyledSharedComponents';
import {
  StyledModalSelectButton,
  StyledFormCloseContainer,
} from './NewSearch/StyledProductFormComponent';


export const renderTableHeader = (header, small, color = 'var(--page-table-header)') => {
  const width = window.innerWidth;

  // Responsive font size logic
  let fontSize = '12px';
  if (small || width <= 950) {
    fontSize = '10px';
  } else if (width <= 1200) {
    fontSize = '12px';
  } else if (width <= 1600) {
    fontSize = '14px';
  } else {
    fontSize = '16px';
  }

  return (
    <StyledText
      uppercase
      size={fontSize}
      color={color}
      weight={600}
      wordBreak="keep-all"
      style={{ letterSpacing: '1px' }}
    >
      {header}
    </StyledText>
  );
};

export const tablePadding = (small) => ({
  horizontal: small ? '1rem' : '1.5rem', vertical: '1.5rem',
});

export const NoDataPlaceholder = ({
  loading,
  loaderColor = 'var(--page-highlight)',
  textColor = 'var(--page-table-text)',
  label,
  boxMargin = '0',
  noShadow = false,
  noRound = false,
  backgroundColor = 'var(--page-table-bg)',
  showRefreshButton = false,
  refreshQuery = () => null,
  noDataLabel = null,
  buttonHighlight = null,
  customButton = null,
  ...rest
}) => {
  const { isDarkMode } = useAppTheme();
  const renderContent = () => {
    const imagePath = isDarkMode ? 'images/logo/logo-full-white.svg' : 'images/logo/logo-full-color.svg';

    if (!loading) {
      return (
        <>
          <Box flex={false} width="10.65rem" height="3rem">
            <Image
              src={localAsset(imagePath)}
              alt="Redflag AI Logo"
              fit="contain"
              width="10.65rem"
              height="3rem"
            />
          </Box>
          <Box width="100%" align="center" justify="center" pad={{ top: '1rem' }}>
            <Box width="50%" align="center">
              <Text color={textColor} size="1rem" style={{ lineHeight: 1.5 }} textAlign="center">
                {(showRefreshButton && noDataLabel) ? noDataLabel : label}
              </Text>
            </Box>
          </Box>
          {showRefreshButton && (
            <Box width="100%" pad={{ top: '1rem' }} align="center">
              <AppButton
                overrideHover
                width="7.25rem"
                height="3rem"
                onClick={() => refreshQuery()}
                level="dynamicLarge"
                color="white"
                bgColor={buttonHighlight}
                fontWeight={600}
                label="Reset"
              />
            </Box>
          )}
          {customButton && !showRefreshButton && (
            <Box width="100%" pad={{ top: '1rem' }} align="center">
              {customButton}
            </Box>
          )}
        </>
      );
    }

    return (
      <Spinning size="large" color={loaderColor} />
    );
  };

  return (
    <StyledRaisedContainer
      background={backgroundColor}
      height="18rem"
      justify="center"
      align="center"
      gap="1rem"
      margin={boxMargin}
      noShadow={noShadow}
      noRound={noRound}
      {...rest}
    >
      {renderContent()}
    </StyledRaisedContainer>
  );
};

NoDataPlaceholder.propTypes = {
  loading: PropTypes.bool.isRequired,
  loaderColor: PropTypes.string,
  textColor: PropTypes.string,
  label: PropTypes.string.isRequired,
  noDataLabel: PropTypes.string,
  boxMargin: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.any.isRequired),
  ]),
  noShadow: PropTypes.bool,
  noRound: PropTypes.bool,
  backgroundColor: PropTypes.string,
  buttonHighlight: PropTypes.string,
  showRefreshButton: PropTypes.bool,
  refreshQuery: PropTypes.func,
  customButton: PropTypes.node,
};

const SelectionDropdown = ({
  handleSelection,
  options,
  label,
  iconPath,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { theme: { page, button } } = useAppTheme();

  return (
    <StyledSimpleDrop
      formIcons
      larger
      highlight={page.focusHighlight}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isOpen={isOpen}
      withCloseDropContent={(onClose) => (
        <Box round="var(--app-radius)" pad="12px" background={page.inputBg}>
          {options.map(({ name, formatted }) => (
            <StyledSelectMenu
              plain
              key={uniqid()}
              label={formatted}
              onClick={() => {
                handleSelection(name);
                onClose();
              }}
              hoverIndicator={button.hoverBg}
              highlightText={button.hoverText}
            />
          ))}
          <StyledSelectMenu
            plain
            key="reset"
            label="Reset"
            onClick={() => {
              handleSelection(null);
              onClose();
            }}
            hoverIndicator={button.hoverBg}
            highlightText={button.hoverText}
          />
        </Box>
      )}
    >
      {() => (
        <AppButton
          fill
          dropDown
          overrideReverse
          textSize="1rem"
          color={page.inputText}
          label={label}
          icon={(
            <StyledSVG
              src={localAsset(iconPath)}
              width="1.1rem"
              height="1.1rem"
              title={label}
              $fillColor={page.inputIcon}
            />
          )}
        />
      )}
    </StyledSimpleDrop>
  );
};

SelectionDropdown.propTypes = {
  handleSelection: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    formatted: PropTypes.string.isRequired,
  }).isRequired).isRequired,
  label: PropTypes.string.isRequired,
  iconPath: PropTypes.string.isRequired,
};

function createInitialState(initialValue) {
  let newValue = '';
  if (initialValue !== '') {
    newValue = initialValue;
  }
  return {
    searchTerm: newValue,
  };
}

function searchReducer(state, action) {
  if (action.type === 'updateTerm') {
    return {
      searchTerm: action.nextTerm,
    };
  }
  if (action.type === 'clearTerm') {
    return {
      searchTerm: '',
    };
  }
  throw Error('Unknown action.');
}

export const SearchInput = ({
  handleSearch,
  initialValue,
  queryKey = null,
  customPlaceholder = 'Search',
}) => {
  const { theme: { page } } = useAppTheme();
  const [state, dispatch] = React.useReducer(searchReducer, initialValue, createInitialState);
  const inputRef = React.useRef();
  const useQueryKey = queryKey ?? 'name';

  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (state.searchTerm !== '' && state.searchTerm !== initialValue) {
        handleSearch(useQueryKey, state.searchTerm, 'i_cont');
      }
    }, 750);

    if (initialValue !== '') {
      inputRef.current.focus();
    }

    return () => clearTimeout(delayDebounceFn);
  }, [state.searchTerm]);

  const handleInputChange = (e) => {
    dispatch({
      type: 'updateTerm',
      nextTerm: e.target.value,
    });
  };

  const handleClear = () => {
    dispatch({ type: 'clearTerm' });
    handleSearch(useQueryKey, null, 'i_cont', false, true);
  };

  return (
    <Box className="relative">
      <StyledTextInput
        withIcon
        ref={inputRef}
        name="search"
        type="text"
        placeholder={customPlaceholder}
        focusHighlight={page.focusHighlight}
        value={state.searchTerm}
        onChange={(e) => handleInputChange(e)}
        icon={(
          <Box pad={{ top: '0.1rem' }}>
            <StyledSVG
              src={localAsset('images/search-input-icon.svg')}
              width="0.85rem"
              height="0.85rem"
              title="Search"
              $fillColor={page.inputIcon}
            />
          </Box>
        )}
      />
      <StyledSearchInputClear
        plain
        icon={<FormClose size="1.25rem" color={page.inputIcon} />}
        onClick={handleClear}
        style={(state.searchTerm !== '') ? null : { display: 'none' }}
      />
    </Box>
  );
};

SearchInput.propTypes = {
  handleSearch: PropTypes.func.isRequired,
  initialValue: PropTypes.string.isRequired,
  queryKey: PropTypes.string,
  customPlaceholder: PropTypes.string,
};

export const SortDropdown = ({
  handleSort,
  sortOptions,
  selectedSortOption = null,
}) => {
  let iconPath = 'images/sort-input-icon-desc.svg';
  const handleSelection = (sortValue) => {
    let useOrder = 'desc';
    if (selectedSortOption && selectedSortOption.direction) {
      useOrder = selectedSortOption.direction === 'asc' ? 'desc' : 'asc';
    }

    handleSort(sortValue, useOrder);
  };

  if (selectedSortOption && selectedSortOption.direction) {
    iconPath = `images/sort-input-icon-${selectedSortOption.direction}.svg`;
  }

  return (
    <SelectionDropdown
      options={sortOptions}
      handleSelection={handleSelection}
      label="Sort"
      iconPath={iconPath}
    />
  );
};

SortDropdown.propTypes = {
  selectedSortOption: PropTypes.shape({
    name: PropTypes.string.isRequired,
    direction: PropTypes.string.isRequired,
  }),
  handleSort: PropTypes.func.isRequired,
  sortOptions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    formatted: PropTypes.string.isRequired,
  }).isRequired).isRequired,
};

export const ModalSelectWrapper = ({
  small,
  setShowModal,
  handleQueryUpdate,
  children,
  label,
  modalCloseMessage = 'Finished',
}) => {
  const { theme: { page, button, navigation } } = useAppTheme();

  return (
    <StyledRaisedContainer
      gap="0.5rem"
      align="center"
      pad="1.5rem"
      width={{ min: small ? '96vw' : '50rem' }}
      background={page.modalBg}
    >
      <Box direction="row" width="100%" justify="between">
        <Text weight={600} size="1rem" color={page.modalText}>
          {label}
        </Text>
        <Button
          plain
          onClick={() => {
            handleQueryUpdate();
            setShowModal(false);
          }}
        >
          <StyledFormCloseContainer highlight={page.highlight} direction="row" gap="0.25rem" align="center">
            <Text weight={600} size="0.9rem" color={page.modalText}>
              Close
            </Text>
            <FormClose color={page.modalText} size="1.2rem" />
          </StyledFormCloseContainer>
        </Button>
      </Box>
      {children}
      <Box
        width="100%"
        pad={{ top: '1rem' }}
        align="center"
        border={{
          color: navigation.border,
          size: '1px',
          style: 'solid',
          side: 'top',
        }}
      >
        <AppButton
          overrideHover
          onClick={() => {
            handleQueryUpdate();
            setShowModal(false);
          }}
          level="dynamicSmall"
          color={button.primaryText}
          bgColor={button.primaryBg}
          fontWeight={600}
          label={modalCloseMessage}
        />
      </Box>
    </StyledRaisedContainer>
  );
};

ModalSelectWrapper.propTypes = {
  small: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  handleQueryUpdate: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  modalCloseMessage: PropTypes.string,
};

export const FilterDropdown = ({
  small,
  handleFilter,
  filterOptions,
  selectedFilters = null,
}) => {
  const { theme: { page, button } } = useAppTheme();
  const [showModal, setShowModal] = React.useState(false);
  const [modalKey, setModalKey] = React.useState(null);
  /* eslint-disable camelcase */
  const [selectedValues, setSelectedValues] = React.useState({
    status: null,
    platforms: [],
    platform: [],
    start_date: [],
    end_date: [],
    result_count: [],
    view_count: [],
    created_at: [],
    down_at: [],
    unit: [],
  });

  const findRangeValues = (key) => {
    const range = _.filter(selectedFilters, ((dt) => dt.name === key));

    const start = _.find(range, ['predicate', 'gteq'])?.value;
    const end = _.find(range, ['predicate', 'lteq'])?.value;

    if (start && end) {
      return [
        {
          name: key,
          value: start,
          predicate: 'gteq',
        },
        {
          name: key,
          value: end,
          predicate: 'lteq',
        },
      ];
    }

    return [];
  };

  React.useEffect(() => {
    if (selectedFilters) {
      const status = _.find(selectedFilters, ['name', 'status'])?.value;
      const platforms = _.find(selectedFilters, ['name', 'platforms'])?.value;
      const platform = _.find(selectedFilters, ['name', 'platform'])?.value;
      const unit = _.find(selectedFilters, ['name', 'unit'])?.value;

      setSelectedValues({
        status: status || null,
        platforms: platforms || [],
        platform: platform || [],
        unit: unit || [],
        start_date: findRangeValues('start_date'),
        end_date: findRangeValues('end_date'),
        result_count: findRangeValues('result_count'),
        view_count: findRangeValues('view_count'),
        created_at: findRangeValues('created_at'),
        down_at: findRangeValues('down_at'),
      });
    }
  }, [selectedFilters]);
  /* eslint-enable camelcase */

  const handleCategorySelect = (uid) => {
    if (uid) {
      setModalKey(uid);
      setShowModal(true);
    } else {
      handleFilter(modalKey, null, null, false, true);
    }
  };

  const handleMultiSelection = (uid) => {
    const prevSelected = { ...selectedValues };
    if (prevSelected[modalKey].includes(uid)) {
      _.pull(prevSelected[modalKey], uid);
    } else {
      prevSelected[modalKey].push(uid);
    }

    setSelectedValues(prevSelected);
  };

  const handleSingleSelection = (uid) => {
    const prevSelected = { ...selectedValues };
    prevSelected[modalKey] = uid;

    setSelectedValues(prevSelected);
  };

  const handleRangeSelection = (rangeStart, rangeEnd) => {
    const prevSelected = { ...selectedValues };
    const rangeArr = [];

    if (rangeStart) {
      rangeArr.push({
        name: modalKey,
        value: rangeStart,
        predicate: 'gteq',
      });
    }

    if (rangeEnd) {
      rangeArr.push({
        name: modalKey,
        value: rangeEnd,
        predicate: 'lteq',
      });
    }
    prevSelected[modalKey] = rangeArr;
    setSelectedValues(prevSelected);
  };

  const currentFilter = _.find(filterOptions, ['name', modalKey]);

  const handleQueryUpdate = (seperateValues = false) => {
    const currentPredicate = currentFilter.validPredicates[0];
    const useValues = selectedValues[modalKey];

    if (useValues?.length > 0) {
      handleFilter(modalKey, useValues, currentPredicate, seperateValues);
    }
  };

  // Helper to normalize validValues to [{ name, formatted }]
  const normalizeValidValues = (validValues) => {
    if (!Array.isArray(validValues)) return [];
    if (typeof validValues[0] === 'string') {
      return validValues.map((val) => ({ name: val, formatted: val }));
    }
    return validValues;
  };

  const { today } = React.useContext(CurrentDateContext);
  const defaultStartDate = format(subDays(today, 2), 'yyyy-MM-dd');
  const defaultEndDate = format(addDays(today, 2), 'yyyy-MM-dd');
  const useSeperate = ['start_date', 'end_date', 'result_count', 'view_count', 'down_at', 'created_at'].includes(modalKey);

  const rangeSelectLabel = (modalKey === 'result_count') ? 'Search results count' : 'View count';

  const renderModalContents = () => {
    const currentValue = selectedValues[modalKey];
    const normalizedOptions = normalizeValidValues(currentFilter?.validValues);

    switch (modalKey) {
      case 'status':
        return (
          <ModalSelectWrapper
            small={small}
            setShowModal={() => setShowModal()}
            handleQueryUpdate={() => handleQueryUpdate()}
            label="Search status"
          >
            <Box wrap direction="row">
              {normalizedOptions?.map(({ name, formatted }) => (
                <Box key={uniqid()} pad={{ right: '1rem', vertical: '0.5rem' }}>
                  <StyledModalSelectButton
                    plain
                    selected={currentValue === name}
                    textSize="1rem"
                    onClick={() => handleSingleSelection(name)}
                    primaryText={page.modalText}
                    buttonHighlight={button.hoverText}
                    buttonBg={button.hoverBg}
                    label={formatted}
                  />
                </Box>
              ))}
            </Box>
          </ModalSelectWrapper>
        );

      case 'platform':
      case 'platforms':
        return (
          <ModalSelectWrapper
            small={small}
            setShowModal={() => setShowModal()}
            handleQueryUpdate={() => handleQueryUpdate()}
            label={modalKey === 'platform' ? 'Result platform' : 'Search platforms'}
          >
            <Box wrap direction="row">
              {normalizedOptions?.map(({ name, formatted }) => (
                <Box key={uniqid()} pad={{ right: '1rem', vertical: '0.5rem' }}>
                  <StyledModalSelectButton
                    plain
                    selected={currentValue?.includes(name)}
                    textSize="1rem"
                    onClick={() => handleMultiSelection(name)}
                    primaryText={page.modalText}
                    buttonHighlight={button.hoverText}
                    buttonBg={button.hoverBg}
                    label={formatted}
                  />
                </Box>
              ))}
            </Box>
          </ModalSelectWrapper>
        );

      case 'unit':
        return (
          <ModalSelectWrapper
            small={small}
            setShowModal={() => setShowModal()}
            handleQueryUpdate={() => handleQueryUpdate()}
            label={currentFilter?.formatted || 'Unit'}
          >
            <Box wrap direction="row">
              {normalizedOptions?.map(({ name, formatted }) => (
                <Box key={uniqid()} pad={{ right: '1rem', vertical: '0.5rem' }}>
                  <StyledModalSelectButton
                    plain
                    selected={currentValue?.includes(name)}
                    textSize="1rem"
                    onClick={() => handleMultiSelection(name)}
                    primaryText={page.modalText}
                    buttonHighlight={button.hoverText}
                    buttonBg={button.hoverBg}
                    label={formatted}
                  />
                </Box>
              ))}
            </Box>
            <Box pad={{ bottom: '0.5rem' }}>
              <Button plain href={paths.unitsSettings} target="_blank" rel="noreferrer">
                <Text size={small ? '0.85rem' : '0.75rem'} color={page.highlight}>
                  Manage
                  {' '}
                  {pluralize(currentFilter?.formatted) || 'units'}
                </Text>
              </Button>
            </Box>
          </ModalSelectWrapper>
        );

      case 'created_at':
      case 'down_at':
      case 'start_date':
      case 'end_date':
        return (
          <DatetimeCalendar
            hideTimeZone
            showCloseButton
            handleClose={() => {
              setShowModal(false);
              handleQueryUpdate(useSeperate);
            }}
            small={small}
            calendarMode="range"
            setDateRange={(from, to) => handleRangeSelection(from, to)}
            dateRangeValue={{
              from: currentValue[0]?.value
                ? new Date(currentValue[0].value)
                : new Date(defaultStartDate),
              to: currentValue[1]?.value
                ? new Date(currentValue[1].value)
                : new Date(defaultEndDate),
            }}
            showTimeSelect={false}
          />
        );

      case 'view_count':
      case 'result_count':
        return (
          <ModalSelectWrapper
            small={small}
            setShowModal={() => setShowModal()}
            handleQueryUpdate={() => handleQueryUpdate(true)}
            label={rangeSelectLabel}
          >
            <Box width="100%" pad={{ vertical: '1rem' }}>
              <StyledRangeSelect
                label={(value) => shortenInt(value)}
                round="var(--app-radius)"
                min={0}
                max={10000}
                color={page.highlight}
                values={[currentValue[0]?.value || 0, currentValue[1]?.value || 10000]}
                onChange={(selected) => handleRangeSelection(selected[0], selected[1])}
              />
            </Box>
          </ModalSelectWrapper>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SelectionDropdown
        options={filterOptions}
        handleSelection={handleCategorySelect}
        label="Filter"
        iconPath="images/filter-input-icon.svg"
      />
      {showModal && (
        <Layer
          responsive={false}
          animate={false}
          onEsc={() => {
            handleQueryUpdate(useSeperate);
            setShowModal(false);
          }}
          onClickOutside={() => {
            handleQueryUpdate(useSeperate);
            setShowModal(false);
          }}
        >
          {renderModalContents(modalKey)}
        </Layer>
      )}
    </>
  );
};

FilterDropdown.propTypes = {
  small: PropTypes.bool.isRequired,
  selectedFilters: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    predicate: PropTypes.string.isRequired,
  })),
  handleFilter: PropTypes.func.isRequired,
  filterOptions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    formatted: PropTypes.string.isRequired,
    validValues: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }),
      ),
      PropTypes.arrayOf(PropTypes.string),
    ]),
    validPredicates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  }).isRequired).isRequired,
};

export const ReviewFilterDropdown = ({
  small,
  handleFilter,
  filterOptions,
  optionNames,
  selectedFilters = null,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [modalKey, setModalKey] = React.useState(null);
  const [selectedValues, setSelectedValues] = React.useState({
    domain: null,
    platforms: [],
  });
  const { theme: { page, button } } = useAppTheme();

  React.useEffect(() => {
    if (selectedFilters) {
      setSelectedValues({
        domain: selectedFilters.domain || null,
        platforms: selectedFilters.platforms || [],
      });
    }
  }, [selectedFilters]);

  const handleCategorySelect = (uid) => {
    if (uid) {
      setModalKey(uid);
      setShowModal(true);
    } else {
      handleFilter(modalKey, null, true);
    }
  };

  const handleMultiSelection = (uid) => {
    const prevSelected = { ...selectedValues };
    if (prevSelected[modalKey].includes(uid)) {
      _.pull(prevSelected[modalKey], uid);
    } else {
      prevSelected[modalKey].push(uid);
    }

    setSelectedValues(prevSelected);
  };

  const handleSingleSelection = (uid) => {
    const prevSelected = { ...selectedValues };
    prevSelected[modalKey] = uid;

    setSelectedValues(prevSelected);
  };

  const currentFilter = _.find(filterOptions, ['name', modalKey]);

  const handleQueryUpdate = () => {
    const useValues = selectedValues[modalKey];

    if (useValues?.length > 0) {
      handleFilter(modalKey, useValues);
    }
  };

  const renderModalContents = () => {
    const currentValue = selectedValues[modalKey];

    switch (modalKey) {
      case 'domain':
        return (
          <ModalSelectWrapper
            small={small}
            setShowModal={() => setShowModal()}
            handleQueryUpdate={() => handleQueryUpdate()}
            label="Result domains"
          >
            <Box direction="column" gap="0.5rem">
              <Text size="1rem" color={page.modalText}>Enter a partial or full domain to filter results:</Text>
              <ProductFormSingleTextInput
                key="domain"
                focusHighlight={page.focusHighlight}
                small={small}
                value={selectedValues.domain || ''}
                handleFormValues={(val) => handleSingleSelection(val)}
              />
            </Box>
          </ModalSelectWrapper>
        );

      case 'platforms':
        return (
          <ModalSelectWrapper
            small={small}
            setShowModal={() => setShowModal()}
            handleQueryUpdate={() => handleQueryUpdate()}
            label="Result platforms"
          >
            <Box wrap direction="row">
              {currentFilter && currentFilter.values.map(({ key, value }) => (
                <Box key={uniqid()} pad={{ right: '1rem', vertical: '0.5rem' }}>
                  <StyledModalSelectButton
                    plain
                    selected={currentValue.includes(key)}
                    textSize="1rem"
                    onClick={() => handleMultiSelection(key)}
                    primaryText={page.modalText}
                    buttonHighlight={button.hoverText}
                    buttonBg={button.hoverBg}
                    label={value}
                  />
                </Box>
              ))}
            </Box>
          </ModalSelectWrapper>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SelectionDropdown
        options={optionNames}
        handleSelection={handleCategorySelect}
        label="Filter"
        iconPath="images/filter-input-icon.svg"
      />
      {showModal && (
        <Layer
          responsive={false}
          animate={false}
          onEsc={() => {
            handleQueryUpdate();
            setShowModal(false);
          }}
          onClickOutside={() => {
            handleQueryUpdate();
            setShowModal(false);
          }}
        >
          {renderModalContents(modalKey)}
        </Layer>
      )}
    </>
  );
};

ReviewFilterDropdown.propTypes = {
  small: PropTypes.bool.isRequired,
  selectedFilters: PropTypes.shape({
    domain: PropTypes.string,
    platforms: PropTypes.arrayOf(PropTypes.string.isRequired),
  }),
  handleFilter: PropTypes.func.isRequired,
  optionNames: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    formatted: PropTypes.string.isRequired,
  }).isRequired).isRequired,
  filterOptions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired),
  }).isRequired).isRequired,
};

const ResultCountDropdown = ({
  handleResultsPerPage,
  resultsPerPageOptions,
  selectedResultsCount,
}) => {
  const { theme: { page, button } } = useAppTheme();
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <StyledSimpleDrop
      formIcons
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isOpen={isOpen}
      highlight={page.focusHighlight}
      withCloseDropContent={(onClose) => (
        <Box round="var(--app-radius)" pad="12px" background={page.inputBg}>
          {resultsPerPageOptions.map((val) => (
            <StyledSelectMenu
              plain
              key={uniqid()}
              label={val}
              onClick={() => {
                handleResultsPerPage(val);
                onClose();
              }}
              hoverIndicator={button.hoverBg}
              highlightText={button.hoverText}
            />
          ))}
        </Box>
      )}
    >
      {(Icon) => (
        <AppButton
          fill
          dropDown
          textSize="1rem"
          color={page.inputText}
          label={selectedResultsCount}
          icon={<Icon color={page.inputIcon} size="1rem" />}
        />
      )}
    </StyledSimpleDrop>
  );
};

ResultCountDropdown.propTypes = {
  selectedResultsCount: PropTypes.number.isRequired,
  handleResultsPerPage: PropTypes.func.isRequired,
  resultsPerPageOptions: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
};

const PaginationSelect = ({
  handlePage,
  currentPage,
  nextPageDisabled,
  zeroBasedPageIndex = false,
}) => {
  const { theme: { page } } = useAppTheme();
  const displayPage = zeroBasedPageIndex ? currentPage + 1 : currentPage;
  const startIndex = zeroBasedPageIndex ? 0 : 1;
  const allowBack = zeroBasedPageIndex ? currentPage > 0 : currentPage > 1;

  const paginate = (pageNum, direction) => {
    if (direction === 'back' && allowBack) {
      handlePage(pageNum - 1);
    } else if (direction === 'forward') {
      handlePage(pageNum + 1);
    }
  };

  return (
    <Box direction="row" gap="0.25rem" align="center">
      <StyledPaginationArrow
        disabled={currentPage === startIndex}
        icon={<Previous size="0.9rem" color={page.inputIcon} />}
        onClick={() => paginate(currentPage, 'back')}
      />
      <Box
        background={page.inputBg}
        width={{ min: '1.6rem' }}
        height="1.6rem"
        pad="0.3rem"
        justify="center"
        align="center"
        border={{
          side: 'all', style: 'solid', size: '1px', color: page.inputBorder,
        }}
      >
        <Text color={page.inputText} textAlign="center" size="0.85rem">{displayPage}</Text>
      </Box>
      <StyledPaginationArrow
        disabled={nextPageDisabled}
        icon={<Next size="0.9rem" color={page.inputIcon} />}
        onClick={() => paginate(currentPage, 'forward')}
      />
    </Box>
  );
};

PaginationSelect.propTypes = {
  currentPage: PropTypes.number.isRequired,
  handlePage: PropTypes.func.isRequired,
  nextPageDisabled: PropTypes.bool.isRequired,
  zeroBasedPageIndex: PropTypes.bool,
};

export const TableWrapper = ({
  small,
  boxPad = null,
  header = null,
  tableControls = null,
  children,
  currentResults = null,
  selectedResultsCount = null,
  totalResults = null,
  handleResultsPerPage = null,
  resultsPerPageOptions = null,
  isPaged = false,
  handlePage = null,
  currentPage = null,
  hidePagination = false,
  zeroBasedPageIndex = false,
  overrideNextPageDisabled = false,
  bgOverride = null,
  ...rest
}) => {
  const { theme: { page } } = useAppTheme();
  const borderProps = { size: '1px', style: 'solid', side: 'bottom' };
  let displayResults = currentResults;
  let nextPageDisabled = false;

  if (isPaged) {
    let startIndex;
    let end;

    if (zeroBasedPageIndex) {
      startIndex = (currentPage * selectedResultsCount) + 1;
      end = Math.min(startIndex + currentResults - 1, totalResults);
    } else {
      startIndex = (currentPage * selectedResultsCount) - (selectedResultsCount - 1);
      end = Math.min(startIndex + (selectedResultsCount - 1), totalResults);
    }

    displayResults = `${startIndex}-${end}`;

    if (currentPage * selectedResultsCount >= totalResults) {
      displayResults = currentResults === 1 ? totalResults : `${startIndex}-${end}`;
    }

    if (typeof overrideNextPageDisabled === 'boolean') {
      nextPageDisabled = overrideNextPageDisabled;
    } else if (typeof totalResults === 'number' && typeof selectedResultsCount === 'number') {
      if (zeroBasedPageIndex) {
        nextPageDisabled = ((currentPage + 1) * selectedResultsCount >= totalResults);
      } else {
        nextPageDisabled = (currentPage * selectedResultsCount >= totalResults);
      }
    } else {
      nextPageDisabled = false;
    }
  }

  const resultsCount = displayResults
    && totalResults
    && parseInt(totalResults, 10) > 0
    && `Viewing ${displayResults} of ${totalResults}`;
  const showPageOptions = resultsPerPageOptions && totalResults > resultsPerPageOptions[0];
  const withResultsPerPage = Boolean(
    handleResultsPerPage && resultsPerPageOptions && showPageOptions,
  );
  const withPartialPagination = Boolean(withResultsPerPage || isPaged);

  // Wrap children in a horizontally scrollable container for mobile
  const mobileTableContent = (
    <Box
      style={{
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        maxWidth: '100vw',
      }}
      width="100%"
    >
      {children}
    </Box>
  );

  const noBottomMargin = (tableControls && small) || !header;

  return (
    <Box background={bgOverride || page.tableBg} pad={boxPad || '0'} {...rest}>
      <Box
        direction="row"
        justify="between"
        border={{ color: page.tableBorder, ...borderProps }}
        margin={{ bottom: noBottomMargin ? '0' : '0.6rem' }}
      >
        {header && (
          <Box
            flex={false}
            border={{ color: page.urlColor, ...borderProps }}
            justify="start"
            pad={{ bottom: '0.5rem' }}
          >
            <Text color={page.tableText} weight={600} size="1rem">
              {header}
            </Text>
          </Box>
        )}
        {tableControls && !small && (
          <Box direction="row" gap="0.5rem" justify="end" pad={{ bottom: '0.5rem' }}>
            {tableControls}
          </Box>
        )}
      </Box>
      {tableControls && small && (
        <Box
          pad={{ vertical: '1rem' }}
          border={{
            color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
          }}
        >
          <Box direction="row" gap="0.5rem" justify="end">
            {tableControls}
          </Box>
        </Box>
      )}
      {small ? mobileTableContent : children}
      {!hidePagination && (
        <Box
          width="100%"
          direction={small ? 'column' : 'row'}
          justify="between"
          align="center"
          gap="1rem"
          pad={{ top: small ? '1rem' : '1.5rem', left: small ? '0' : '0.5rem' }}
        >
          {Boolean(resultsCount) && (
            <Text color={page.hintText} size={small ? '0.9rem' : '0.75rem'} textAlign={small ? 'center' : 'start'}>
              {resultsCount}
            </Text>
          )}
          {withPartialPagination && (
            <Box
              direction="row"
              gap="3rem"
              pad={{ right: small ? '0' : '0.5rem' }}
              justify={(small && isPaged) ? 'between' : 'end'}
              width={(small || !resultsCount) ? '100%' : '50%'}
            >
              {withResultsPerPage && (
                <Box direction="row" gap="0.25rem" align="center">
                  <Box align="start" justify="center">
                    <Text color={page.tableText} size={small ? '0.9rem' : '0.75rem'} style={{ whiteSpace: 'nowrap' }}>
                      Show per page
                    </Text>
                  </Box>
                  <ResultCountDropdown
                    selectedResultsCount={selectedResultsCount}
                    handleResultsPerPage={handleResultsPerPage}
                    resultsPerPageOptions={resultsPerPageOptions}
                  />
                </Box>
              )}
              {isPaged && (
                <Box flex={false} align="center" justify="end">
                  <PaginationSelect
                    handlePage={handlePage}
                    currentPage={currentPage}
                    nextPageDisabled={nextPageDisabled}
                    zeroBasedPageIndex={zeroBasedPageIndex}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

TableWrapper.propTypes = {
  small: PropTypes.bool.isRequired,
  header: PropTypes.string,
  boxPad: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.any.isRequired),
  ]),
  children: PropTypes.node.isRequired,
  tableControls: PropTypes.node,
  currentResults: PropTypes.number,
  selectedResultsCount: PropTypes.number,
  totalResults: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  handleResultsPerPage: PropTypes.func,
  resultsPerPageOptions: PropTypes.arrayOf(PropTypes.number.isRequired),
  handlePage: PropTypes.func,
  isPaged: PropTypes.bool,
  currentPage: PropTypes.number,
  hidePagination: PropTypes.bool,
  zeroBasedPageIndex: PropTypes.bool,
  overrideNextPageDisabled: PropTypes.bool,
  bgOverride: PropTypes.string,
};
